import React, { useState, useEffect } from 'react';
import { Shield, Key, Users, User, Settings, Mail, ArrowLeft } from 'lucide-react';
import { Spinner } from '../components/common/LoadingComponents';
import { authAPI } from '../services/api';
import api from '../services/api';
import '../styles/Login.css';

// Google icon SVG component
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Microsoft icon SVG component
const MicrosoftIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20">
    <path fill="#F25022" d="M1 1h10v10H1z"/>
    <path fill="#00A4EF" d="M1 13h10v10H1z"/>
    <path fill="#7FBA00" d="M13 1h10v10H13z"/>
    <path fill="#FFB900" d="M13 13h10v10H13z"/>
  </svg>
);

// Custom/Generic SSO icon
const SSOIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
  </svg>
);

// Animated background with gradient blobs
const AnimatedBackground = () => (
  <div className="animated-bg">
    <div className="blob blob-1"></div>
    <div className="blob blob-2"></div>
    <div className="blob blob-3"></div>
    <div className="blob blob-4"></div>
  </div>
);

// Demo notice banner at top
const DemoNoticeBanner = ({ demoInfo }) => {
  if (!demoInfo || !demoInfo.demoMode) return null;

  return (
    <div className="demo-notice-banner">
      <strong>Demo Mode</strong>
      <span>Data resets every {demoInfo.resetInterval}. Use any account below to sign in.</span>
    </div>
  );
};

// Demo accounts grid at bottom
const DemoAccountsGrid = ({ demoInfo, onSelectAccount }) => {
  if (!demoInfo || !demoInfo.demoMode) return null;

  const getAccountIcon = (type) => {
    if (type === 'Administrator') return <Settings size={16} />;
    if (type.includes('Household')) return <Users size={16} />;
    return <User size={16} />;
  };

  const getAccountTypeClass = (type) => {
    if (type === 'Administrator') return 'demo-account-admin';
    if (type === 'Household Manager') return 'demo-account-manager';
    if (type === 'Household Member') return 'demo-account-member';
    return 'demo-account-private';
  };

  return (
    <div className="demo-accounts-section">
      <div className="demo-accounts-header">
        <span>Quick Login — Click to select account</span>
      </div>
      <div className="demo-accounts-grid">
        {demoInfo.accounts.map((account, index) => (
          <button
            key={index}
            className={`demo-account-chip ${getAccountTypeClass(account.type)}`}
            onClick={() => onSelectAccount(account.email)}
            type="button"
          >
            <span className="demo-chip-icon">
              {getAccountIcon(account.type)}
            </span>
            <span className="demo-chip-info">
              <span className="demo-chip-email">{account.email.split('@')[0]}</span>
              <span className="demo-chip-type">{account.type}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

// Helper to get the icon for a provider
const getProviderIcon = (providerId) => {
  switch (providerId) {
    case 'google':
      return <GoogleIcon />;
    case 'microsoft':
      return <MicrosoftIcon />;
    case 'custom':
      return <SSOIcon />;
    default:
      return <SSOIcon />;
  }
};

function Login({ onLogin, onServerUnavailable }) {
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(null);
  const [error, setError] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [oauthProviders, setOauthProviders] = useState([]);
  const [demoInfo, setDemoInfo] = useState(null);
  const [loginMethod, setLoginMethod] = useState(null); // null = method selection, 'email' = email form
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  // Apply user's saved theme preference on mount, or detect system preference for new users
  useEffect(() => {
    const root = document.documentElement;
    
    // Check for saved theme mode, otherwise detect system preference
    const savedMode = localStorage.getItem('theme-mode');
    const mode = savedMode || (window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
    
    root.setAttribute('data-theme', mode);
    
    if (mode === 'dark') {
      // Load user's saved dark background preference, default to True Black
      const savedDarkBg = localStorage.getItem('theme-dark-bg');
      const darkBg = savedDarkBg 
        ? JSON.parse(savedDarkBg) 
        : { bg: '#000000', surface: '#1a1a1a', border: '#2a2a2a' }; // True Black default
      
      root.style.setProperty('--background-color', darkBg.bg);
      root.style.setProperty('--surface-color', darkBg.surface);
      root.style.setProperty('--border-color', darkBg.border);
    } else {
      // Light mode defaults
      root.style.setProperty('--background-color', '#f8fafc');
      root.style.setProperty('--surface-color', '#ffffff');
      root.style.setProperty('--border-color', '#e2e8f0');
    }
    
    // Load user's saved accent color preference
    const savedAccent = localStorage.getItem('theme-accent');
    if (savedAccent) {
      const accent = JSON.parse(savedAccent);
      root.style.setProperty('--primary-color', accent.value);
      root.style.setProperty('--primary-hover', accent.hover);
      const primaryTextColor = accent.textColor === 'dark' ? '#1a1a1a' : '#ffffff';
      root.style.setProperty('--primary-text-color', primaryTextColor);
    }
  }, []);

  // Check for maintenance message and load OAuth providers on mount
  useEffect(() => {
    const maintenanceMsg = localStorage.getItem('maintenance_message');
    if (maintenanceMsg) {
      setError(maintenanceMsg);
      localStorage.removeItem('maintenance_message');
    }

    // Load enabled OAuth providers
    loadOAuthProviders();
    
    // Load demo mode info
    loadDemoInfo();
  }, []);

  const loadOAuthProviders = async () => {
    try {
      const response = await api.get('/oauth/providers');
      // Now providers is an array of objects with id, name, buttonLabel
      setOauthProviders(response.data.providers || []);
    } catch (err) {
      console.log('OAuth providers not available');
      setOauthProviders([]);
    }
  };

  const loadDemoInfo = async () => {
    try {
      const response = await api.get('/setup/demo');
      setDemoInfo(response.data);
    } catch (err) {
      console.log('Demo info not available');
      setDemoInfo(null);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleDemoAccountSelect = (email) => {
    setFormData({
      email: email,
      password: demoInfo.password
    });
    setLoginMethod('email');
    setError('');
  };

  const handleOAuthLogin = async (providerId) => {
    setOauthLoading(providerId);
    setError('');

    try {
      const response = await api.get(`/oauth/${providerId}/authorize`);
      const { authUrl, state } = response.data;

      sessionStorage.setItem('oauth_state', state);
      sessionStorage.setItem('oauth_provider', providerId);

      window.location.href = authUrl;
    } catch (err) {
      console.error('OAuth error:', err);
      setError(err.response?.data?.error || `Failed to connect to provider`);
      setOauthLoading(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authAPI.login({
        email: formData.email,
        password: formData.password,
        mfaToken: mfaRequired ? mfaToken : undefined,
        isBackupCode: mfaRequired ? useBackupCode : undefined
      });

      // Check if MFA is required
      if (response.data.mfaRequired) {
        setMfaRequired(true);
        setLoading(false);
        return;
      }

      const { user, household, token } = response.data;

      localStorage.setItem('auth_token', token);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('household', JSON.stringify(household));

      onLogin(user, household);

    } catch (err) {
      console.error('Auth error:', err);
      
      // Check if this is a network/server error (no response or network error)
      if (!err.response || err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED') {
        // Server is unavailable - trigger the server unavailable screen
        if (onServerUnavailable) {
          onServerUnavailable();
          return;
        }
        setError('Unable to connect to server. Please try again later.');
      } else if (err.response?.status === 503 && err.response?.data?.maintenanceMode) {
        setError(err.response.data.message);
      } else {
        const message = err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Authentication failed';
        setError(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setMfaRequired(false);
    setMfaToken('');
    setUseBackupCode(false);
    setError('');
  };

  const handleBackToMethodSelection = () => {
    setLoginMethod(null);
    setError('');
    setFormData({ email: '', password: '' });
  };

  // Check if we have multiple login options available
  const hasMultipleOptions = oauthProviders.length > 0;

  // MFA verification form
  if (mfaRequired) {
    return (
      <div className="login-page">
        <AnimatedBackground />
        <div className="login-stack">
          <div className={`login-container ${loading ? 'login-loading' : ''}`}>
            <div className="login-header">
              <h1>Remidy</h1>
              <p>Two-factor authentication required</p>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="login-form">
              <div className="mfa-icon-container">
                {useBackupCode ? <Key size={48} /> : <Shield size={48} />}
              </div>

              <div className="form-group">
                <label htmlFor="mfaToken">
                  {useBackupCode ? 'Backup Code' : 'Authentication Code'}
                </label>
                <input
                  type="text"
                  id="mfaToken"
                  value={mfaToken}
                  onChange={(e) => setMfaToken(e.target.value.replace(/\s/g, ''))}
                  placeholder={useBackupCode ? 'Enter backup code' : 'Enter 6-digit code'}
                  required
                  disabled={loading}
                  autoComplete="one-time-code"
                  className={`mfa-input ${loading ? 'input-disabled' : ''}`}
                  maxLength={useBackupCode ? 10 : 6}
                />
                {!useBackupCode && (
                  <small className="form-hint">Enter the code from your authenticator app</small>
                )}
              </div>

              <button type="submit" className="btn-primary btn-login" disabled={loading}>
                {loading ? (
                  <>
                    <Spinner size="sm" />
                    <span>Verifying...</span>
                  </>
                ) : (
                  'Verify'
                )}
              </button>
            </form>

            <div className="login-footer mfa-footer">
              <button 
                onClick={() => setUseBackupCode(!useBackupCode)} 
                className="link-button"
                disabled={loading}
              >
                {useBackupCode ? 'Use authenticator app instead' : 'Use a backup code instead'}
              </button>
              <button 
                onClick={handleBackToLogin} 
                className="link-button"
                disabled={loading}
              >
                Back to login
              </button>
            </div>

            {loading && (
              <div className="login-loading-overlay">
                <div className="login-loading-content">
                  <Spinner size="xl" />
                  <span>Verifying code...</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Method selection screen (when multiple options available and no method selected)
  if (hasMultipleOptions && loginMethod === null) {
    return (
      <div className="login-page">
        <AnimatedBackground />
        <div className="login-stack">
          {/* Demo Notice Banner */}
          <DemoNoticeBanner demoInfo={demoInfo} />

          {/* Login Container - Method Selection */}
          <div className={`login-container ${oauthLoading ? 'login-loading' : ''}`}>
            <div className="login-header">
              <h1>Remidy</h1>
              <p>Welcome back</p>
            </div>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}

            <div className="login-method-buttons">
              {oauthProviders.map(provider => (
                <button 
                  key={provider.id}
                  type="button"
                  className={`btn-login-method btn-${provider.id}`}
                  onClick={() => handleOAuthLogin(provider.id)}
                  disabled={oauthLoading}
                >
                  {oauthLoading === provider.id ? (
                    <Spinner size="md" />
                  ) : (
                    getProviderIcon(provider.id)
                  )}
                  <span>Continue with {provider.buttonLabel}</span>
                </button>
              ))}
              
              <button 
                type="button"
                className="btn-login-method btn-email"
                onClick={() => setLoginMethod('email')}
                disabled={oauthLoading}
              >
                <Mail size={20} />
                <span>Continue with Email</span>
              </button>
            </div>

            {/* Loading overlay */}
            {oauthLoading && (
              <div className="login-loading-overlay">
                <div className="login-loading-content">
                  <Spinner size="xl" />
                  <span>Signing you in...</span>
                  <span className="login-loading-subtext">Please wait while we complete your authentication.</span>
                </div>
              </div>
            )}
          </div>

          {/* Demo Accounts Grid */}
          <DemoAccountsGrid 
            demoInfo={demoInfo} 
            onSelectAccount={handleDemoAccountSelect}
          />
        </div>
      </div>
    );
  }

  // Email login form (either selected or only option)
  return (
    <div className="login-page">
      <AnimatedBackground />
      <div className="login-stack">
        {/* Demo Notice Banner */}
        <DemoNoticeBanner demoInfo={demoInfo} />

        {/* Login Container */}
        <div className={`login-container ${loading ? 'login-loading' : ''}`}>
          <div className="login-header">
            {hasMultipleOptions && (
              <button 
                type="button"
                className="back-to-methods"
                onClick={handleBackToMethodSelection}
                disabled={loading}
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <h1>Remidy</h1>
            <p>{hasMultipleOptions ? 'Sign in with email' : 'Welcome back'}</p>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
                autoComplete="email"
                className={loading ? 'input-disabled' : ''}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                disabled={loading}
                autoComplete="current-password"
                className={loading ? 'input-disabled' : ''}
              />
            </div>

            <button type="submit" className="btn-primary btn-login" disabled={loading}>
              {loading ? (
                <>
                  <Spinner size="sm" />
                  <span>Signing In...</span>
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Loading overlay */}
          {loading && (
            <div className="login-loading-overlay">
              <div className="login-loading-content">
                <Spinner size="xl" />
                <span>Signing you in...</span>
                <span className="login-loading-subtext">Please wait while we complete your authentication.</span>
              </div>
            </div>
          )}
        </div>

        {/* Demo Accounts Grid */}
        <DemoAccountsGrid 
          demoInfo={demoInfo} 
          onSelectAccount={handleDemoAccountSelect}
        />
      </div>
    </div>
  );
}

export default Login;
